import type { WineDetail } from "@corklog/shared";
import { useTranslation } from "react-i18next";

import { countryName } from "../countries.js";
import { formatDecimal } from "../decimal.js";
import { Flag } from "./Flag.js";

/**
 * What the catalogue knows about the wine itself, read by everybody.
 *
 * **Added 2026-09-21, because half of it was only visible from inside the
 * form that changes it.** The country, the type, the base vintage, the
 * disgorgement date and the dosage were all asked for at registration, stored,
 * carried in the contract — and shown on no page. The owner could see them by
 * opening *Ändra vinet*, which is not reading, it is standing in the edit
 * screen and being careful not to touch anything; a guest or a husgäst, who
 * may not open that screen at all, could not see them by any means. A cellar
 * that asks for a disgorgement date and then tells nobody is asking for
 * nothing.
 *
 * So the facts get a home of their own that is not a form. Labelled, because
 * an unlabelled `2019` beside an unlabelled `6` is a puzzle; a plain
 * definition list rather than a panel, per `design-direction.md` — rows and
 * hairlines, not cards.
 *
 * **Only what is known.** A row per field with an em dash where nobody has
 * said would be a list that is mostly dashes in this cellar: the alcohol is
 * recorded for a handful of wines, disgorgement for the Champagnes and for
 * nothing else. The absent row is the honest answer, and `Luckor` is the
 * screen that exists to say what is missing.
 *
 * The identity line above the band keeps producer, region, vintage and style
 * — that line answers *which bottle is this*, and repeating it here would make
 * the page say everything twice.
 */
export function WineFacts({ wine }: { wine: WineDetail }) {
  const { t } = useTranslation();

  const facts: { key: string; label: string; value: React.ReactNode }[] = [];

  facts.push({
    key: "country",
    label: t("wines.country"),
    // The flag is the same mark the wine list uses, so a country is
    // recognised the same way in both places.
    value: (
      <span className="flex items-baseline gap-1.5">
        <Flag countryCode={wine.countryCode} />
        {countryName(wine.countryCode)}
      </span>
    ),
  });

  // The type is stored in English — `Red`, `SweetWhite` — because the schema
  // is English like the rest of the code. Every other screen that shows it
  // goes through `wineType.*`; this one said `Red` to a Swedish reader until
  // 2026-09-22.
  facts.push({ key: "type", label: t("wines.type"), value: t(`wineType.${wine.type}`) });

  if (wine.grapes.length > 0) {
    // Joined the way the page already joined them when they stood alone.
    facts.push({
      key: "grapes",
      label: t("wines.grapes"),
      value: wine.grapes.join(" · "),
    });
  }

  if (wine.alcoholPercent !== null && wine.alcoholPercent !== undefined) {
    facts.push({
      key: "alcohol",
      label: t("wine.fact.alcohol"),
      value: `${formatDecimal(wine.alcoholPercent)} %`,
    });
  }

  if (wine.systembolagetId !== null) {
    facts.push({
      key: "systembolagetId",
      label: t("wine.fact.systembolagetId"),
      value: wine.systembolagetId,
    });
  }

  // The three Champagne facts, which is why they are last: they are absent on
  // every still wine in the cellar, and a list should not open with the fields
  // that are usually empty.
  if (wine.baseVintage !== null && wine.baseVintage !== undefined) {
    facts.push({
      key: "baseVintage",
      label: t("wines.baseVintage"),
      value: String(wine.baseVintage),
    });
  }

  if (wine.disgorgedOn !== null && wine.disgorgedOn !== undefined) {
    facts.push({
      key: "disgorgedOn",
      label: t("wines.disgorgedOn"),
      value: wine.disgorgedOn,
    });
  }

  if (wine.dosageGramsPerLitre !== null && wine.dosageGramsPerLitre !== undefined) {
    facts.push({
      key: "dosage",
      label: t("wine.fact.dosage"),
      value: `${formatDecimal(wine.dosageGramsPerLitre)} g/l`,
    });
  }

  return (
    // Two columns that do not move with the content: the labels set the first
    // one and the values all start at the same place, which is what makes a
    // list of facts readable as a column rather than as sentences. Figures are
    // tabular so the years and the grams line up under each other.
    <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
      {facts.map((fact) => (
        <div key={fact.key} className="contents">
          <dt className="text-muted">{fact.label}</dt>
          <dd className="tabular-nums">{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}
